import { Stack, Group, Text, Flex, Anchor, Box, Divider } from "@mantine/core"
import styles from "./AppFooter.module.css"
import { AEMLeafNode, AEMPageType } from "@AEM/models/AEMPage.model"
import { useRedirect } from "@hooks/useRedirect"
import { AEMImage } from "@components/AEMImage"
import { useAuthContext } from "@context/AuthProvider"
import { LinkType } from "@models/link.model"
import { eventTrackingItems } from "@utils/eventTracking"
import { useAEMNavData } from "@AEM/hooks/useAEMNavData"
import { useAEMFooterData } from "@AEM/hooks/useAEMFooterData"
function AppFooter({ useDivider }: { useDivider?: boolean }) {
	const mappedDataQaAttribute = (link: string): string => {
		const base = "footer.link."
		switch (link) {
			case "/privacy-policy":
				return `${base}privacy`
			case "/terms-of-use":
				return `${base}terms`
			case "/travel-agent-guidelines":
				return `${base}guidelines`
			default:
				break
		}
		if (link.includes("about")) {
			return `${base}about`
		}
		return `${base}${link}`
	}

	const { footerData } = useAEMFooterData()
	const { data: navigationFooterComponentData } = useAEMNavData(
		AEMPageType.NAVIGATION_FOOTER
	)

	const { isAuthenticated } = useAuthContext()
	const { handleRedirect } = useRedirect()

	const footerClick = (
		linkdata: Partial<AEMLeafNode>,
		action: "logo" | "link"
	) => {
		const interactionType = action === "logo" ? "Logo click" : "Link click"
		eventTrackingItems({
			event_name: "footer_click",
			category: "Footer Interaction",
			interactionType: interactionType,
			clickText: linkdata?.label || "",
			link_url: linkdata?.link
		})
	}

	const navigationItems =
		(navigationFooterComponentData?.[0]?.children.items as AEMLeafNode[]) ?? []

	return (
		<Stack
			gap={"40px"}
			align={"center"}
			className={styles["footer-root"]}
			px={"lg"}
			py={"40"}
			id="about-royal"
		>
			{useDivider && (
				<Divider className={styles["footer-divider"]} size={16} px={"xl"} />
			)}
			<Flex
				direction={{ base: "column", sm: "row" }}
				align={"center"}
				gap={{ base: "sm", sm: "xl" }}
			>
				{navigationItems
					.map((item) => (item?.link ? item : { ...item, link: "" }))
					.map((link: Partial<AEMLeafNode>, index: number) => {
						if (link.type === "onetrust-type")
							return (
								<Anchor
									className={styles["footer-link"]}
									key={index}
									fz={12}
									id="ot-sdk-btn"
									fw={500}
									c={"white"}
									component={"button"}
									onClick={() => {
										// Trigger OneTrust Cookie Preferences Modal
										if (
											window.OneTrust &&
											typeof window.OneTrust.ToggleInfoDisplay === "function"
										) {
											window.OneTrust.ToggleInfoDisplay()
										} else if (
											window.Optanon &&
											typeof window.Optanon.ToggleInfoDisplay === "function"
										) {
											window.Optanon.ToggleInfoDisplay()
										}
									}}
									data-qa={mappedDataQaAttribute(link?.link ?? "")}
								>
									{link?.label}
								</Anchor>
							)
						// [PROVISIONAL SOLUTION] - This should be differentiated from other links with the data coming from AEM
						if (
							!isAuthenticated &&
							link.type !== "public" &&
							link.link === "/travel-agent-guidelines"
						)
							return null

						if (isAuthenticated && link.link === "/register-agency") return null
						return (
							<Anchor
								className={styles["footer-link"]}
								key={index}
								fz={12}
								fw={500}
								c={"white"}
								component={"button"}
								onClick={() => {
									footerClick(link, "link")
									handleRedirect({
										link: link.link,
										type: link.type as LinkType
									})
								}}
								data-qa={mappedDataQaAttribute(link?.link ?? "")}
							>
								{link.label}
							</Anchor>
						)
					})}
			</Flex>
			<Group
				justify="center"
				align="flex-end"
				className={styles["footer-rcg-logo"]}
			>
				<Box className={styles.separator} />
				<AEMImage imageData={footerData?.footerLogo} w={220} h={80} mb={-10} />
				<Box className={styles.separator} />
			</Group>
			<Flex
				direction={{ base: "column", sm: "row" }}
				justify={"center"}
				align={"center"}
				gap={{ base: "lg", sm: "xl" }}
			>
				<AEMImage
					w={102}
					h={27}
					imageData={{
						...(footerData?.subsidiaryLogos?.[0] || { path: "", title: "" })
					}}
				/>
				<AEMImage
					w={137}
					h={28}
					imageData={{
						...(footerData?.subsidiaryLogos?.[1] || { path: "", title: "" })
					}}
				/>
				<AEMImage
					w={98}
					h={10}
					imageData={{
						...(footerData?.subsidiaryLogos?.[2] || { path: "", title: "" })
					}}
				/>
			</Flex>
			<Text
				fz={11}
				fw={500}
				lh={2.2}
				c={"white"}
				ta={{ base: "center", sm: "left" }}
				id="copyright"
			>
				{footerData?.copyrightText}
			</Text>
		</Stack>
	)
}

export default AppFooter
